import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { Hall, User } from '../api/types';
import { OCCASIONS } from '../content/occasions';
import { useT } from '../i18n';
import { CONTACT_EMAIL, CONTACT_PHONE, EMAIL_HREF, PHONE_HREF } from '../lib/contact';
import { moneyShort } from '../lib/format';
import { startingPrice } from '../lib/price';
import { getTheme, setTheme, type Theme } from '../lib/theme';
import { CurtainMark } from './Brand';
import { ConfirmModal } from './ConfirmModal';
import { placeName } from '../lib/places';

/** The public site's sections, in the order the menu lists them. */
export const SECTIONS = [
  { to: '/', key: 'snHome', end: true },
  { to: '/venues', key: 'snExplore' },
  { to: '/occasions', key: 'snEventTypes' },
  { to: '/locations', key: 'snLocations' },
  { to: '/inspiration', key: 'snInspiration' },
  { to: '/about', key: 'snAbout' },
] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  halls: Hall[];
  user: User | null;
  venueLink: { to: string; label: string } | null;
  onLogout: () => void;
}

/**
 * The full-screen curtain menu. Two deep-teal curtains draw across the screen,
 * then the numbered section links rise in; the side column carries a live
 * venue spotlight, occasion shortcuts, the booking CTA, language, theme and
 * the account. Opened by the header button (desktop) or the dock (phones).
 */
export function SiteMenu({ open, onClose, halls, user, venueLink, onLogout }: Props) {
  const { t, lang, setLang, locale } = useT();
  const { pathname } = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Closed, the menu is still in the DOM (the curtains animate out): keep it
  // out of the tab order and away from assistive tech. React 18's types do not
  // know `inert`, so it is set directly.
  useEffect(() => {
    rootRef.current?.toggleAttribute('inert', !open);
  }, [open]);
  const [theme, setThemeState] = useState<Theme>(() => getTheme());
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  // Focus the close button once the curtains have drawn (desktop shows it).
  useEffect(() => {
    if (!open) return;
    setThemeState(getTheme());
    const id = window.setTimeout(() => {
      const el = closeRef.current;
      if (el && getComputedStyle(el).display !== 'none') el.focus({ preventScroll: true });
    }, 650);
    return () => window.clearTimeout(id);
  }, [open]);

  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    for (const h of halls) {
      const c = (h.city || '').trim();
      if (c) counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([c]) => c);
  }, [halls]);

  // The best-rated venue with a photo, else the newest one with a photo.
  const spot = useMemo(() => {
    const withPhoto = halls.filter((h) => h.photos.length > 0);
    if (!withPhoto.length) return null;
    return [...withPhoto].sort(
      (a, b) => (b.review_count > 0 ? 1 : 0) - (a.review_count > 0 ? 1 : 0) || b.rating - a.rating || b.created_at - a.created_at,
    )[0];
  }, [halls]);

  const subs: Record<(typeof SECTIONS)[number]['to'], string> = {
    '/': t('mmHomeSub'),
    '/venues': halls.length ? t('mmExploreSub', { count: halls.length }) : t('mmExploreSubNone'),
    '/occasions': t('mmEventsSub', { count: OCCASIONS.length }),
    '/locations': cities.length ? t('mmLocationsSub', { cities: cities.slice(0, 2).map((c) => placeName(c, lang)).join(', ') }) : t('mmLocationsSubNone'),
    '/inspiration': t('mmInspirationSub'),
    '/about': t('mmAboutSub'),
  };

  const flipTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setThemeState(next);
  };

  const spotPrice = spot ? startingPrice(spot) : null;
  const spotLine = spot
    ? [
        [spot.locality, spot.city].filter(Boolean).join(', ') || spot.location,
        t('vcGuests', { count: spot.capacity.toLocaleString(locale) }),
        spotPrice && `${t('vcFrom').toLowerCase()} ${moneyShort(spotPrice.amount)} ${t(spotPrice.unit)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  // The second CTA follows the visitor: list a venue, their venues, or bookings.
  const second = venueLink ?? (user ? { to: '/bookings', label: t('mmMyBookings') } : null);

  return (
    <div
      className={`mobile-menu ds${open ? ' is-open' : ''}`}
      id="site-menu"
      ref={rootRef}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      aria-label={t('mmMenu')}
    >
      <div className="mm-inner">
        <div className="mm-top">
          <span className="mm-label">{t('mmMenu')}</span>
          <button ref={closeRef} className="mm-close" type="button" aria-label={t('navCloseMenu')} onClick={onClose}>
            <span>{t('mmClose')}</span>
            <i aria-hidden="true" />
          </button>
        </div>

        <nav className="mm-nav" aria-label={t('mmMenu')}>
          {SECTIONS.map((s, i) => (
            <NavLink key={s.to} to={s.to} end={'end' in s ? s.end : false} style={{ '--i': i } as CSSProperties} onClick={onClose}>
              <b>{String(i + 1).padStart(2, '0')}</b>
              <span className="mm-t">{t(s.key)}</span>
              <small>{subs[s.to]}</small>
            </NavLink>
          ))}
        </nav>

        <div className="mm-side">
          {spot && (
            <Link className="mm-feature" to={`/hall/${spot.id}`} onClick={onClose}>
              <img src={spot.photos[0]} alt="" loading="lazy" decoding="async" />
              <span className="mmf-tag">{t('mmSpotlight')}</span>
              <span className="mmf-body">
                <b>{spot.name}</b>
                <span>{spotLine}</span>
              </span>
            </Link>
          )}

          <div className="mm-block">
            <span className="mm-h">{t('mmOccasions')}</span>
            <div className="mm-chips">
              {OCCASIONS.map((o) => (
                <Link key={o.slug} to={`/occasions/${o.slug}`} onClick={onClose}>
                  {t(o.title)}
                </Link>
              ))}
            </div>
          </div>

          <div className="mm-cta">
            <Link
              className="mm-book"
              to="/venues"
              onClick={(e) => {
                onClose();
                // Already on the venues page: going "there" would look like
                // nothing happened, so take them to its search box instead.
                if (pathname === '/venues') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  window.setTimeout(() => document.querySelector<HTMLInputElement>('.page-hero input')?.focus({ preventScroll: true }), 700);
                }
              }}
            >
              <span>
                <small>{t('mmReady')}</small>
                {t('snBook')}
              </span>
              <i className="ri-arrow-right-up-line i" aria-hidden="true" />
            </Link>
            {second && (
              <Link className="mm-list" to={second.to} onClick={onClose}>
                <i className="ri-building-2-line i" aria-hidden="true" />
                <span>{second.label}</span>
              </Link>
            )}
          </div>

          <div className="mm-utils">
            <button type="button" onClick={() => setLang(lang === 'ml' ? 'en' : 'ml')} lang={lang === 'ml' ? 'en' : 'ml'}>
              <i className="ri-global-line i" aria-hidden="true" />
              <span>{lang === 'ml' ? t('languageEnglish') : t('languageMalayalam')}</span>
            </button>
            <button type="button" onClick={flipTheme}>
              <i className={`${theme === 'dark' ? 'ri-sun-line' : 'ri-moon-line'} i`} aria-hidden="true" />
              <span>{theme === 'dark' ? t('navLightMode') : t('navDarkMode')}</span>
            </button>
            {user ? (
              <button
                type="button"
                onClick={() => setConfirmingLogout(true)}
              >
                <i className="ri-logout-box-r-line i" aria-hidden="true" />
                <span>{t('menuLogout')}</span>
              </button>
            ) : (
              <Link className="mm-login" to="/login" onClick={onClose}>
                <i className="ri-user-line i" aria-hidden="true" />
                <span>{t('snLogin')}</span>
              </Link>
            )}
          </div>

          <div className="mm-contact">
            <span>{t('mmTalk')}</span>
            <a href={PHONE_HREF}>
              <i className="ri-phone-line" aria-hidden="true" />
              {CONTACT_PHONE}
            </a>
            <a href={EMAIL_HREF}>
              <i className="ri-mail-line" aria-hidden="true" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </div>
      <CurtainMark className="mm-mark" />
      {confirmingLogout && (
        <ConfirmModal
          title={t('menuLogoutConfirmTitle')}
          body={t('menuLogoutConfirmBody')}
          confirmLabel={t('menuLogoutConfirmAction')}
          danger
          onConfirm={() => {
            setConfirmingLogout(false);
            onClose();
            onLogout();
          }}
          onClose={() => setConfirmingLogout(false)}
        />
      )}
    </div>
  );
}
